import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { selectWidgetsDataState } from 'widgets/store/selectors.ts';
import State from 'store/State';
import { IndexEventsDataState } from './IndexEventsDataState';
import {
    IndexEventsRequest,
    toUid,
} from 'widgets/indexEvents/api/IndexEventsRequest';
import { IndexEventsResponse } from 'widgets/indexEvents/api/IndexEventsResponse';
import { validateResponse } from 'widgets/indexEvents/api/validation';

const initialState: IndexEventsDataState = {};

const indexEventsDataSlice = createSlice({
    name: 'indexEventsDataSlice',
    initialState,
    reducers: {
        addRequest: (
            state,
            action: PayloadAction<{
                request: IndexEventsRequest;
            }>
        ) => {
            const { request } = action.payload;
            const uid = toUid(request);
            state[uid] = {
                request,
                requestTimestamp: new Date().getTime(),
                status: 'In Progress',
            };
        },
        addResponse: (
            state,
            action: PayloadAction<{
                request: IndexEventsRequest;
                response: IndexEventsResponse;
            }>
        ) => {
            const {
                payload: { request, response },
            } = action;
            const uid = toUid(request);
            if (!state[uid]) {
                throw new Error('No request for uid ' + uid);
            }
            state[uid].response = response;
            state[uid].responseTimestamp = new Date().getTime();

            const responseValidationResult = validateResponse(response);
            if (responseValidationResult.isValid) {
                state[uid].status = 'Completed';
            } else {
                state[uid].status = 'Failed';
                state[uid].error = responseValidationResult.error;
            }
        },
        clearData: (
            state,
            action: PayloadAction<
                | {
                      request: IndexEventsRequest;
                  }
                | string
            >
        ) => {
            if (typeof action.payload === 'string') {
                delete state[action.payload];
                return;
            }
            const {
                payload: { request },
            } = action;
            const uid = toUid(request);
            delete state[uid];
        },

        addError: (
            state,
            action: PayloadAction<{
                request: IndexEventsRequest;
                error: unknown;
            }>
        ) => {
            const {
                payload: { error, request },
            } = action;
            const uid = toUid(request);
            state[uid].error = error;
            state[uid].status = 'Failed';
            state[uid].responseTimestamp = new Date().getTime();
        },
    },
});

export default indexEventsDataSlice.reducer;

export const { addError, addRequest, addResponse, clearData } =
    indexEventsDataSlice.actions;

export const selectIndexEventsWidgetsData = (state: State) =>
    selectWidgetsDataState(state).indexEvents;

export const selectIndexEventsRequestDataState = (
    state: State,
    requestUid: string | undefined
) => (requestUid ? selectIndexEventsWidgetsData(state)[requestUid] : undefined);
